import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import AnimatedPage from "@/components/AnimatedPage";
import NavHeader from "@/components/NavHeader";
import { CalendarIcon, Edit2 } from "lucide-react";
import { format, differenceInMonths, differenceInWeeks, addMonths } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { getChitPoolById, updateChitPool } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { ChitPool } from "@/lib/types";

const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 5 characters." }),
  description: z.string().optional(),
  amount: z.coerce
    .number()
    .min(1000, { message: "Amount must be at least 10000." }),
  months: z.coerce
    .number()
    .min(3, { message: "Months must be at least 3." })
    .max(60, { message: "Months must be at most 60." }),
  monthlyAmount: z.coerce
    .number()
    .min(0, { message: "Monthly amount must be at least 0." }),
  postLiftAmount: z.coerce
    .number()
    .min(0, { message: "Post lift amount must be at least 0." }),
  firstDueDate: z.date({ required_error: "First due date is required." }),
  intervalType: z.enum(["weekly", "monthly"]).default("monthly"),
  intervalValue: z.coerce.number().min(1).max(12).default(1),
});

const EditChitPool = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [monthsCompleted, setMonthsCompleted] = useState(0);
  const [sameAmountMonthly, setSameAmountMonthly] = useState(true);
  const [showMonthlyAmountsModal, setShowMonthlyAmountsModal] = useState(false);
  const [monthlyAmountsArray, setMonthlyAmountsArray] = useState<{ month: number; amount: number }[]>([]);
  const [chitPool, setChitPool] = useState<ChitPool | null>(null);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      amount: 0,
      months: 12,
      monthlyAmount: 0,
      postLiftAmount: 0,
      firstDueDate: new Date(),
    },
  });

  const amount = form.watch("amount");
  const months = form.watch("months");
  const firstDueDate = form.watch("firstDueDate");
  const intervalType = form.watch("intervalType");
  const intervalValue = form.watch("intervalValue");
  
  useEffect(() => {
    const fetchChitPool = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const fetchedChitPool = await getChitPoolById(id);
        setChitPool(fetchedChitPool);
        
        // Set form values
        form.reset({
          name: fetchedChitPool.name,
          description: fetchedChitPool.description || "",
          amount: fetchedChitPool.amount,
          months: fetchedChitPool.months,
          monthlyAmount: fetchedChitPool.monthlyAmount,
          postLiftAmount: fetchedChitPool.postLiftAmount,
          firstDueDate: new Date(fetchedChitPool.firstDueDate),
          intervalType: fetchedChitPool.intervalType || "monthly",
          intervalValue: fetchedChitPool.intervalValue || 1,
        });
        
        // Set monthly amounts configuration
        setSameAmountMonthly(fetchedChitPool.sameAmountMonthly !== false);
        
        if (fetchedChitPool.monthlyAmounts) {
          setMonthlyAmountsArray(fetchedChitPool.monthlyAmounts);
        } else {
          // Initialize monthly amounts array
          const initialAmounts = Array.from({ length: fetchedChitPool.months }, (_, i) => ({
            month: i + 1,
            amount: fetchedChitPool.monthlyAmount
          }));
          setMonthlyAmountsArray(initialAmounts);
        }
      } catch (error) {
        console.error("Error fetching chit pool:", error);
        toast.error("Failed to load chit pool details");
        navigate("/chit-pools");
      } finally {
        setLoading(false);
      }
    };

    fetchChitPool();
  }, [id, navigate]);

  useEffect(() => {
    if (sameAmountMonthly && amount && months) {
      const monthlyAmount = Math.round(amount / months);
      const postLiftAmount = Math.round(monthlyAmount * 1.1); // Example: 10% more after lifting
      
      form.setValue("monthlyAmount", monthlyAmount);
      form.setValue("postLiftAmount", postLiftAmount);
    }
  }, [amount, months, sameAmountMonthly, form]);

  // Update monthly amounts array when months change
  useEffect(() => {
    if (months && monthlyAmountsArray.length !== months) {
      const currentLength = monthlyAmountsArray.length;
      if (months > currentLength) {
        // Add new months
        const newMonths = Array.from({ length: months - currentLength }, (_, i) => ({
          month: currentLength + i + 1,
          amount: 0
        }));
        setMonthlyAmountsArray([...monthlyAmountsArray, ...newMonths]);
      } else {
        // Remove extra months
        setMonthlyAmountsArray(monthlyAmountsArray.slice(0, months));
      }
    }
  }, [months, monthlyAmountsArray.length]);

  // Calculate months completed based on first due date and interval
  useEffect(() => {
    if (firstDueDate) {
      const today = new Date();
      if (firstDueDate < today) {
        let completed = 0;
        if (intervalType === 'weekly') {
          completed = Math.max(0, differenceInWeeks(today, firstDueDate));
        } else {
          const monthsDiff = differenceInMonths(today, firstDueDate);
          completed = Math.floor(monthsDiff / intervalValue);
        }
        setMonthsCompleted(completed);
      } else {
        setMonthsCompleted(0);
      }
    }
  }, [firstDueDate, intervalType, intervalValue]);

  const handleMonthlyAmountChange = (month: number, amount: number) => {
    setMonthlyAmountsArray(prev => 
      prev.map(item => 
        item.month === month ? { ...item, amount } : item
      )
    );
  };

  const calculateTotalFromMonthlyAmounts = () => {
    return monthlyAmountsArray.reduce((sum, item) => sum + item.amount, 0);
  };

  const handleSaveMonthlyAmounts = () => {
    // Don't auto-set amount from monthly amounts - keep it user-editable
    // Set monthlyAmount and postLiftAmount to 0 when using different amounts
    if (!sameAmountMonthly) {
      form.setValue("monthlyAmount", 0);
      form.setValue("postLiftAmount", 0);
    }
    setShowMonthlyAmountsModal(false);
    toast.success("Monthly amounts saved successfully");
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!id) return;
    
    try {
      setIsSubmitting(true);
      
      const updateData: any = {
        name: values.name,
        description: values.description || "",
        amount: values.amount,
        months: values.months,
        monthlyAmount: sameAmountMonthly ? values.monthlyAmount : 0,
        postLiftAmount: values.postLiftAmount, // Always save post-lift amount
        firstDueDate: values.firstDueDate.toISOString(),
        sameAmountMonthly,
        monthlyAmounts: !sameAmountMonthly ? monthlyAmountsArray : null,
        intervalType: values.intervalType,
        intervalValue: values.intervalValue,
      };
      
      await updateChitPool(id, updateData);
      
      toast.success("Chit pool updated successfully");
      navigate(`/chit-pools/${id}`);
    } catch (error) {
      toast.error("Failed to update chit pool");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AnimatedPage>
        <div className="container max-w-3xl py-8 flex justify-center items-center min-h-[50vh]">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading chit pool details...</p>
          </div>
        </div>
      </AnimatedPage>
    );
  }

  return (
    <AnimatedPage>
      <div className="container max-w-3xl py-8">
        <NavHeader
          title="Edit Chit Pool"
          subtitle="Update your chit pool details"
          backTo={`/chit-pools/${id}`}
        />

        <Card className="bg-card/60 backdrop-blur-sm border border-border/50">
          <CardContent className="pt-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Chit Pool Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter chit pool name" {...field} className="bg-background" />
                      </FormControl>
                      <FormDescription className="text-left">
                        A descriptive name for your chit pool
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Enter chit pool description (optional)" 
                          className="resize-none bg-background" 
                          {...field} 
                        />
                      </FormControl>
                      <FormDescription className="text-left">
                        Additional details about this chit pool
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex items-center space-x-2 py-4 px-1">
                  <Switch
                    id="same-amount"
                    checked={sameAmountMonthly}
                    onCheckedChange={setSameAmountMonthly}
                  />
                  <label
                    htmlFor="same-amount"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Same amount for all months
                  </label>
                </div>

                {!sameAmountMonthly && (
                  <div className="p-4 border rounded-lg bg-muted/50">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium">Monthly Amounts Configuration</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Total: ₹{calculateTotalFromMonthlyAmounts().toLocaleString()}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowMonthlyAmountsModal(true)}
                      >
                        <Edit2 className="h-4 w-4 mr-2" />
                        Edit Amounts
                      </Button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="intervalType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Payment Interval</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="bg-background">
                              <SelectValue placeholder="Select interval type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription className="text-left">
                          How often payments will be collected
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {intervalType === 'monthly' && (
                    <FormField
                      control={form.control}
                      name="intervalValue"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Interval Frequency</FormLabel>
                          <Select 
                            onValueChange={(value) => field.onChange(parseInt(value))} 
                            value={field.value?.toString()}
                          >
                            <FormControl>
                              <SelectTrigger className="bg-background">
                                <SelectValue placeholder="Select frequency" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="1">Every Month</SelectItem>
                              <SelectItem value="2">Every 2 Months</SelectItem>
                              <SelectItem value="3">Every 3 Months</SelectItem>
                              <SelectItem value="4">Every 4 Months</SelectItem>
                              <SelectItem value="6">Every 6 Months</SelectItem>
                              <SelectItem value="12">Every 12 Months</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormDescription className="text-left">
                            Payment frequency in months
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="amount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Total Amount</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="Enter total amount"
                            {...field}
                            className="bg-background"
                          />
                        </FormControl>
                        <FormDescription className="text-left">
                          The total amount of the chit pool
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="months"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Duration ({intervalType === 'weekly' ? 'Weeks' : 'Periods'})</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder={`Enter number of ${intervalType === 'weekly' ? 'weeks' : 'periods'}`}
                            {...field}
                            className="bg-background"
                          />
                        </FormControl>
                        <FormDescription className="text-left">
                          Total number of {intervalType === 'weekly' ? 'weeks' : intervalValue > 1 ? `${intervalValue}-month periods` : 'months'} for this chit pool
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {sameAmountMonthly && (
                    <>
                      <FormField
                        control={form.control}
                        name="monthlyAmount"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Monthly Amount</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                placeholder="Enter monthly amount"
                                {...field}
                                className="bg-background"
                              />
                            </FormControl>
                            <FormDescription className="text-left">
                              Amount to be paid monthly before lifting
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="postLiftAmount"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Post-Lift Monthly Amount</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                placeholder="Enter post-lift amount"
                                {...field}
                                className="bg-background"
                              />
                            </FormControl>
                            <FormDescription className="text-left">
                              Amount to be paid monthly after lifting
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  )}
                </div>

                {!sameAmountMonthly && (
                  <FormField
                    control={form.control}
                    name="postLiftAmount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Post-Lift Contribution Amount</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="Enter post-lift contribution amount"
                            {...field}
                            className="bg-background"
                          />
                        </FormControl>
                        <FormDescription className="text-left">
                          Amount to be paid after a member has lifted
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={form.control}
                  name="firstDueDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>First Due Date</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant={"outline"}
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {field.value ? (
                                format(field.value, "PPP")
                              ) : (
                                <span>Pick a date</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            initialFocus
                            className={cn("p-3 pointer-events-auto")}
                          />
                        </PopoverContent>
                      </Popover>
                      <FormDescription className="text-left">
                        {monthsCompleted > 0 
                          ? `This chit pool started ${monthsCompleted} months ago` 
                          : "The date when the first payment is due"}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end space-x-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(`/chit-pools/${id}`)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting && (
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                    )}
                    Update Chit Pool
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Amounts Modal */}
      <Dialog open={showMonthlyAmountsModal} onOpenChange={setShowMonthlyAmountsModal}>
        <DialogContent className="max-w-2xl max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configure Monthly Amounts</DialogTitle>
            <DialogDescription>
              Specify the amount to be collected for each month
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {monthlyAmountsArray.map((item) => {
              const dueDate = addMonths(firstDueDate, item.month - 1);
              return (
                <div key={item.month} className="flex items-center gap-4">
                  <label className="text-sm font-medium w-32">
                    {format(dueDate, "MMM dd, yyyy")}:
                  </label>
                  <Input
                    type="number"
                    value={item.amount}
                    onChange={(e) => handleMonthlyAmountChange(item.month, parseFloat(e.target.value) || 0)}
                    placeholder="Enter amount"
                    className="flex-1"
                  />
                </div>
              );
            })}
          </div>
          <DialogFooter className="mt-6">
            <div className="flex justify-between w-full">
              <div className="text-sm">
                <span className="font-medium">Total: </span>
                <span className="text-primary">₹{calculateTotalFromMonthlyAmounts().toLocaleString()}</span>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowMonthlyAmountsModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleSaveMonthlyAmounts}
                >
                  Save
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AnimatedPage>
  );
};

export default EditChitPool;